import { assertContext } from '@bos/utils';
import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useState } from 'react';

import type { AppAdvancedSearchForm } from '../../../../../components/AppAdvancedSearch/helpers/getAppAdvancedSearchFormSchema';

export type AdvancedSearchTab = 'client' | 'account';

type AdvancedClientSearchContextParams = {
	isAdvancedClientSearchOpen: boolean;
	searchData: AppAdvancedSearchForm;
	accountSearchValue: string;
	activeSearchTab: AdvancedSearchTab;
	handleOpenAdvancedClientSearch: () => void;
	handleCloseAdvancedClientSearch: () => void;
	handleToggleAdvancedClientSearch: () => void;
	handleSetSearchData: (searchValues: AppAdvancedSearchForm) => void;
	handleSetAccountSearchValue: (value: string) => void;
	handleSetActiveSearchTab: (tab: AdvancedSearchTab) => void;
};

type AdvancedClientSearchProviderParams = {
	children: ReactNode;
};

const AdvancedClientSearchContext =
	createContext<AdvancedClientSearchContextParams | null>(null);

export const AdvancedClientSearchProvider = ({
	children,
}: AdvancedClientSearchProviderParams) => {
	const [isAdvancedClientSearchOpen, setIsAdvancedClientSearchOpen] =
		useState<boolean>(false);

	const [searchData, setSearchData] = useState<AppAdvancedSearchForm>({});

	const [accountSearchValue, setAccountSearchValue] = useState('');

	const [activeSearchTab, setActiveSearchTab] =
		useState<AdvancedSearchTab>('client');

	const handleOpenAdvancedClientSearch = () => {
		setIsAdvancedClientSearchOpen(true);
	};

	const handleCloseAdvancedClientSearch = () => {
		setIsAdvancedClientSearchOpen(false);
	};

	const handleToggleAdvancedClientSearch = () => {
		setIsAdvancedClientSearchOpen((prev) => !prev);
	};

	const handleSetSearchData = useCallback(
		(searchValues: AppAdvancedSearchForm) => {
			setSearchData(searchValues);
		},
		[],
	);

	const handleSetAccountSearchValue = useCallback((value: string) => {
		setAccountSearchValue(value);
	}, []);

	const handleSetActiveSearchTab = useCallback((tab: AdvancedSearchTab) => {
		setActiveSearchTab(tab);
	}, []);

	const value: AdvancedClientSearchContextParams = {
		isAdvancedClientSearchOpen,
		searchData,
		accountSearchValue,
		activeSearchTab,
		handleOpenAdvancedClientSearch,
		handleCloseAdvancedClientSearch,
		handleToggleAdvancedClientSearch,
		handleSetSearchData,
		handleSetAccountSearchValue,
		handleSetActiveSearchTab,
	};

	return (
		<AdvancedClientSearchContext.Provider value={value}>
			{children}
		</AdvancedClientSearchContext.Provider>
	);
};

export const useAdvancedClientSearchContext = () => {
	const context = useContext(AdvancedClientSearchContext);
	assertContext(context, 'AdvancedClientSearchContext');
	return context;
};
